import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  HelpOutline as HelpOutlineIcon,
  VpnKey as PasswordIcon,
  Logout as LogoutIcon,
  Delete as DeleteIcon,
  SwapHoriz as SwitchIcon,
} from '@mui/icons-material';
import SupportTicketDialog from '../../chat/SupportTicketDialog';
import { useTherapistUiContext } from '../../../context/TherapistUiContext';
import { TOPBAR_HEIGHT } from './navConfig';
import NotificationBell from '../../common/NotificationBell';

// Thin (56px) top bar for the sidebar shell. Keeps only what genuinely belongs
// at the top: hamburger (mobile), logo, page title, help, and the account menu
// (with multi-role "Switch View"). Navigation lives in the Sidebar.

const hasRole = (user, role) =>
  !!user?.role && (Array.isArray(user.role) ? user.role.includes(role) : user.role === role);

const TopBar = ({ user, onLogout, onChangePassword, onDeleteAccount, pageTitle, onMenuClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);

  // Where-am-I context for Help / Report-an-issue. Merges the route with what
  // the mounted surfaces registered (selected client, client-detail tab,
  // selected conversation) — see context/TherapistUiContext.js.
  const { getReportContext } = useTherapistUiContext();
  const reportContext = supportOpen ? getReportContext() : null;

  const showSupervisor = hasRole(user, 'clinical_supervisor');
  const showResearcher = hasRole(user, 'researcher');
  const showAdmin = hasRole(user, 'admin');

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          backgroundColor: '#FFFFFF',
          color: '#111827',
          boxShadow: 'none',
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        <Toolbar sx={{ minHeight: `${TOPBAR_HEIGHT}px !important`, px: { xs: 1, sm: 2 } }}>
          {isMobile && (
            <IconButton edge="start" aria-label="Open navigation" onClick={onMenuClick} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
          )}

          <RouterLink
            to="/therapist/dashboard"
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', marginRight: 16, flexShrink: 0 }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/logo-desktop.png`}
              srcSet={`${process.env.PUBLIC_URL}/logo-desktop.png 1x, ${process.env.PUBLIC_URL}/logo-desktop2x.png 2x`}
              alt="AIMHI Logo"
              style={{ height: 32, width: 'auto' }}
            />
          </RouterLink>

          {!isMobile && (
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {pageTitle}
            </Typography>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <NotificationBell />

          <Tooltip title="Get help / report an issue">
            <IconButton color="inherit" onClick={() => setSupportOpen(true)} data-testid="therapist-help-button">
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>

          <IconButton
            aria-label="account of current user"
            aria-controls="topbar-account-menu"
            aria-haspopup="true"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            color="inherit"
            data-testid="user-menu-button"
          >
            <AccountCircle />
          </IconButton>

          <Menu
            id="topbar-account-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            keepMounted
          >
            <MenuItem disabled>
              <Typography variant="caption">{user?.email}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setAnchorEl(null); onChangePassword(); }}>
              <ListItemIcon><PasswordIcon fontSize="small" /></ListItemIcon>
              Change Password
            </MenuItem>

            {(showSupervisor || showResearcher || showAdmin) && <Divider />}
            {showSupervisor && (
              <MenuItem component={RouterLink} to="/clinical-supervisor/dashboard" onClick={() => setAnchorEl(null)}>
                <ListItemIcon><SwitchIcon fontSize="small" /></ListItemIcon>
                Switch to Clinical Supervisor View
              </MenuItem>
            )}
            {showResearcher && (
              <MenuItem component={RouterLink} to="/researcher/dashboard" onClick={() => setAnchorEl(null)}>
                <ListItemIcon><SwitchIcon fontSize="small" /></ListItemIcon>
                Switch to Researcher View
              </MenuItem>
            )}
            {showAdmin && (
              <MenuItem component={RouterLink} to="/admin/dashboard" onClick={() => setAnchorEl(null)}>
                <ListItemIcon><SwitchIcon fontSize="small" /></ListItemIcon>
                Switch to Admin View
              </MenuItem>
            )}

            <Divider />
            <MenuItem onClick={() => { onLogout(); setAnchorEl(null); }} data-testid="logout-button">
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { onDeleteAccount(); setAnchorEl(null); }} sx={{ color: 'error.main' }}>
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              Delete Account
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <SupportTicketDialog
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        token={localStorage.getItem('token')}
        currentPage={reportContext?.currentPage || 'other'}
        extraContext={reportContext || { userRole: 'therapist' }}
        reportingFrom={reportContext?.uiLocation}
      />
    </>
  );
};

export default TopBar;
